import { ConfigStateService, SessionStateService } from '@abp/ng.core';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';

import 'src/core/services/auth/oauth-service.extensions';
import { CAConfigStateService } from 'src/core/services/config/ca-config-state.service';
import {
  appendEmbeddedMode,
  enableExternalView,
  hasEmbeddedModeQuery,
  resolveInternalRedirect,
} from 'src/app/external-bootstrap/external-view-state';
import { resolveLanguage } from '../language-utils';

/**
 * External login provider name for the direct-embedding (iframe) integration model.
 * Matches `CAAuthServerConstants.AzureB2CEmbeddedOpenIdConnectName` on the backend.
 */
const AZURE_B2C_EMBEDDED_PROVIDER = 'Azure B2C Embedded';

@Component({
  selector: 'ca-external-main',
  templateUrl: './external-main.component.html',
  styleUrls: ['./external-main.component.scss'],
  standalone: false,
})
export class ExternalMainComponent implements OnInit {
  get hasLoggedIn(): boolean {
    return this.oAuthService.hasValidAccessToken();
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly oAuthService: OAuthService,
    private readonly configStateService: CAConfigStateService,
    private readonly abpConfigStateService: ConfigStateService,
    private readonly sessionStateService: SessionStateService
  ) {
    const refreshToken = this.route.snapshot.queryParams.refreshToken;
    const redirectTo = this.route.snapshot.queryParams.redirectTo ?? '';
    const language = this.route.snapshot.queryParams.lang ?? '';
    const loginHint = this.route.snapshot.queryParams.loginHint ?? '';
    const orgId = this.route.snapshot.queryParams.orgId ?? '';
    const isEmbedded = hasEmbeddedModeQuery(window.location.search);
    const tenant =
      this.route.snapshot.queryParams['__tenant'] ??
      this.route.snapshot.queryParams.tenantId ??
      this.route.snapshot.queryParams.tenant ??
      '';
    const navigationUrl = this.buildNavigationUrl(redirectTo, language, tenant, isEmbedded);

    enableExternalView();

    const localization = this.abpConfigStateService.getOne('localization');
    const supportedLanguages = localization?.languages ?? [];
    this.sessionStateService.setLanguage(resolveLanguage(language, supportedLanguages));
    this.setTenantContext(tenant);

    if (this.hasLoggedIn) {
      // Use window.location.replace instead of router.navigate to break the ABP
      // navigation loop: ABP's navigateToPreviousUrl() subscribes to the ongoing
      // currentUser observable and re-navigates to /external each time
      // refreshAppState() emits, which would re-instantiate this component and
      // cause a 404. A hard navigation starts Angular fresh at the correct route
      // with tokens already in storage — no ABP loop possible.
      this.configStateService.refreshAppState().subscribe(() => {
        window.location.replace(navigationUrl);
      });
      return;
    }

    if (refreshToken) {
      this.oAuthService
        .fetchTokenUsingGrant('refresh_token', {
          refresh_token: refreshToken,
        })
        .then(() => {
          this.configStateService.refreshAppState().subscribe(() => {
            window.location.replace(navigationUrl);
          });
        });

      return;
    }

    // Path B — Direct-embedding (iframe) integration model:
    // The customer embeds the CA Angular app in an iframe and passes `loginHint` and
    // `orgId` as query parameters derived from the customer portal's Azure AD token.
    // We navigate the iframe to the AuthServer external login challenge URL so the
    // AuthServer can establish a session via the embedded Azure B2C provider.
    // `loginHint` and `orgId` are intentionally excluded from the ReturnUrl so that
    // the flow is not re-triggered after the AuthServer redirects back.
    if (loginHint) {
      const returnUrl = this.buildReturnUrl(redirectTo, language, tenant, isEmbedded);
      const challengeUrl = this.buildLoginHintChallengeUrl(loginHint, orgId, tenant, returnUrl);
      window.location.href = challengeUrl;
      return;
    }

    const currentUrl = window.location.pathname + window.location.search;
    this.oAuthService.initCodeFlow(encodeURIComponent(currentUrl));
  }

  ngOnInit(): void {}

  // ---------------------------------------------------------------------------
  // Direct-embedding (loginHint) helpers — Path B
  // ---------------------------------------------------------------------------

  /**
   * Builds the ReturnUrl the AuthServer redirects back to after a successful
   * external login challenge. Includes `redirectTo`, `lang`, and `__tenant` but
   * deliberately omits `loginHint` and `orgId` to avoid re-triggering the challenge.
   */
  private buildReturnUrl(
    redirectTo: string,
    lang: string,
    tenant: string,
    embedded: boolean
  ): string {
    const params = new URLSearchParams();
    if (redirectTo) {
      params.set('redirectTo', redirectTo);
    }
    if (lang) {
      params.set('lang', lang);
    }
    if (tenant) {
      params.set('__tenant', tenant);
    }
    appendEmbeddedMode(params, embedded);
    const queryString = params.toString();
    return window.location.origin + '/external' + (queryString ? '?' + queryString : '');
  }

  /**
   * Builds the full AuthServer external login challenge URL for the direct-embedding
   * (iframe) integration model.
   */
  private buildLoginHintChallengeUrl(
    loginHint: string,
    orgId: string,
    tenant: string,
    returnUrl: string
  ): string {
    const authServerBaseUrl = this.oAuthService.tokenEndpoint.replace('/connect/token', '');
    const params = new URLSearchParams({
      handler: 'ExternalLogin',
      provider: AZURE_B2C_EMBEDDED_PROVIDER,
      login_hint: loginHint,
      ReturnUrl: returnUrl,
    });
    if (tenant) {
      params.set('tenantId', tenant);
    }
    if (orgId) {
      params.set('selectedOrgId', orgId);
    }
    return `${authServerBaseUrl}/Account/Login?${params.toString()}`;
  }

  private buildNavigationUrl(
    redirectTo: string,
    language: string,
    tenant: string,
    embedded: boolean
  ): string {
    const url = resolveInternalRedirect(redirectTo);

    if (language) {
      url.searchParams.set('lang', language);
    }

    if (tenant) {
      url.searchParams.set('__tenant', tenant);
    }

    appendEmbeddedMode(url.searchParams, embedded);

    return `${url.pathname}${url.search}${url.hash}`;
  }

  private setTenantContext(tenantId: string): void {
    if (!tenantId) {
      return;
    }

    this.sessionStateService.setTenant({
      id: tenantId,
      isAvailable: true,
    });
  }
}
